import React, { useEffect, useState } from 'react';

import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { errors, isValid, validator } from 'react-validator';

import { TeacherAssignmentTypeEnum } from '@school_ify/schoolify-repository/src/teacher/types';
import { FileFragment } from '@school_ify/schoolify-repository/src/shared/types';

import pushRouterHelper from 'hooks/useRouterHelper';
import { useAppSelector } from 'redux/hooks';
import { liveClassStudentsState, unSelectStudents } from 'redux/liveClass/students.slices';
import { IModel } from 'utils/IStudy';
import Editor from 'components/TestEditor/Editor';
import Errors from 'components/Errors/Errors';
import Popover from 'components/Popover/Popover';
import TeachingStructureDropDown from 'components/TeachingStructure/TeachingStructureDropDown';
import useSaveStudy from 'views/Study/components/Forms/useSaveStudy';
import FileUploader from 'clean/components/common/FileUploader/FileUploader';
import DatePicker from 'components/Input/DatePicker/DatePicker';

import Students from '../../../LiveClass/components/TeacherDashboard/components/students/Index';

import '../Pages/StudyEdit.scoped.scss';

const Material: React.FC<{
  subject;
  data: IModel;
  assignmentFiles: FileFragment[];
  title: string;
}> = ({ subject, data, title, assignmentFiles }) => {
  const [validation, setValidation] = useState({});
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const { query } = pushRouterHelper();
  const [model, setModel] = useState<IModel>({
    ...data,
    type: TeacherAssignmentTypeEnum.MATERIAL,
  });
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { saveStudy, setFiles, files } = useSaveStudy();

  const { selectedStudents } = useAppSelector(liveClassStudentsState);

  function setModelProp(e, prop) {
    const copyMod = { ...model };
    copyMod[prop] = e;
    setModel(copyMod);
  }

  const cancel = () => {
    setModel({ ...data, type: TeacherAssignmentTypeEnum.MATERIAL });

    selectedStudents.length > 0 && dispatch(unSelectStudents());
    history.push(`/study/details/${subject}?title=${title}&class=${query.class as string}`);
  };

  async function save() {
    validator(
      { model, selected_students: selectedStudents },
      {
        'model.title': ['required', 'string'],
        'model.duration': ['required', 'integer', 'between:1,1000'],
        'model.description': ['required'],
        'model.teaching_structure_id': ['required', 'string'],
      },
      {
        'model.title.required': t('study:title_required'),
        'model.duration.required': t('study:duration_required'), //
        'model.duration.between': t('study:duration_between'),
        'model.description.required': t('study:description_required'), //
        'model.teaching_structure_id.required': t('study:structure_id_required'),
        'selected_students.min': t('study:students_min'),
      }
    );

    setValidation(errors());
    if (!isValid()) {
      return;
    }
    const assignmentId = await saveStudy(model);
    history.push(
      `/study/details/${subject}?lesson=${assignmentId}&title=${title}&type=${model.type}&class=${
        query.class as string
      }`
    );
  }

  useEffect(() => {
    setModel({
      ...data,
      type: TeacherAssignmentTypeEnum.MATERIAL,
    });
  }, [data]);

  useEffect(() => {
    if (assignmentFiles) {
      setFiles(
        assignmentFiles.map(file => {
          const dummyFile = new File([''], file.file_name);
          Object.defineProperty(dummyFile, 'id', {
            value: file.id,
            writable: false,
          });
          return dummyFile;
        })
      );
    }
  }, [assignmentFiles]);

  return (
    <>
      <div className="grid grid-col-2 sm-grid-col-1 gap-1" style={{ position: 'relative' }}>
        <div>
          <label htmlFor="title">{t('study:title')}</label>
          <input
            type="text"
            className="full-input"
            value={model.title}
            onChange={e => {
              setModelProp(e.target.value, 'title');
            }}
            placeholder={t('study:title').toString().replace('*', '')}
            id={'title'}
          />
          <Errors errors={validation} prop={'model.title'} />
        </div>
        <div>
          <label htmlFor="title">{t('study:chapter')}</label>
          <div style={{ display: 'block' }}>
            {subject && (
              <>
                <TeachingStructureDropDown
                  key={model.teaching_structure_id}
                  teaching_structure_id={subject}
                  selected={model.teaching_structure_id}
                  onSelect={val => {
                    setModelProp(val.id, 'teaching_structure_id');
                  }}
                />
              </>
            )}
          </div>
          <div className={'mt-1'}>
            <Errors prop={'model.teaching_structure_id'} errors={validation} />
          </div>
        </div>
        <div>
          <label htmlFor="due_at">{t('study:due_at')}:</label>
          <DatePicker
            value={model.due_at}
            onChange={({ dateStr }) => {
              setModelProp(dateStr, 'due_at');
            }}
          />
          <div className="mb-1">
            <Errors prop={'model.due_at'} errors={validation} />
          </div>
        </div>
        <div>
          <label htmlFor="Duration">{t('study:duration')}</label>
          <input
            type="number"
            className="full-input"
            value={model.duration}
            placeholder={t('study:duration_placeholder')}
            onChange={e => {
              setModelProp(e.target.value, 'duration');
            }}
            id={'Duration'}
          />
          <Errors errors={validation} prop={'model.duration'} />
        </div>
      </div>
      <div className="mt-1 mb-1">
        <label htmlFor="content">{t('study:Content')}</label>

        <Editor
          value={model.description}
          callback={val => {
            setModel({
              ...model,
              description: val,
            });
          }}
        />
        <Errors errors={validation} prop={'model.description'} />
      </div>
      <div className="mt-1 mb-1">
        <label htmlFor="content">{t('study:Additional_documents')}</label>
        <FileUploader readOnly={false} files={files} setFiles={setFiles} />
      </div>
      <div className="mb-1">
        <Students />
        <Errors errors={validation} prop={'selected_students'} />
      </div>
      <div className="flex mt-2 justify-center">
        <label
          className="cancel-btn btn rounded-full nowrap no-underline text-bold mr-1"
          onClick={cancel}
        >
          Cancel
        </label>
        <label
          className="save-btn bg-primary text-white btn rounded-full no-underline text-bold mr-1"
          onClick={save}
        >
          Save
        </label>
        {deleteModal && (
          <div className="deleteModal">
            <Popover
              style={{
                top: '0%',
                bottom: '0%',
                zIndex: '999',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <p className="delete-pop-over mb-1 ">
                Are you sure you want to delete additional documents?
              </p>
              <div className="flex gap-1 justify-between">
                <button className="btn nowrap shadow border-none w-100 text-white rounded-full no-underline text-bold bg-primary">
                  Yes
                </button>
                <div style={{ width: '5%' }} />
                <button
                  // style={{ ...button, border: '2px solid #F04F48', background: '#F04F48' }}
                  className="btn nowrap shadow border-none w-100 text-white rounded-full no-underline text-bold bg-red"
                  onClick={() => setDeleteModal(false)}
                >
                  No
                </button>
              </div>
            </Popover>
          </div>
        )}
      </div>
    </>
  );
};

export default Material;
